import React from 'react';
import { ArrowLeft, Globe as GlobeIcon, Users as UsersIcon, Calendar as CalendarIcon, MessageSquare as MessageSquareIcon, Building2 as Building2Icon, ExternalLink as ExternalLinkIcon } from 'lucide-react';
import { companies, people } from '../../data/mockData';
interface CompanyProfileProps {
  companyId: string;
  onNavigate: (page: string, id?: string) => void;
}
export function CompanyProfile({
  companyId,
  onNavigate
}: CompanyProfileProps) {
  const company = companies.find(c => c.id === companyId);
  if (!company) {
    return <div className="text-center py-20 text-slate-500" data-id="element-536">
        Unternehmen nicht gefunden.
      </div>;
  }
  const teamMembers = people.filter(p => p.companyId === company.id);
  return <div className="space-y-6" data-id="element-537">
      <button onClick={() => onNavigate('network-companies')} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors" data-id="element-538">
        <ArrowLeft size={16} data-id="element-539" />
        Zurück zum Verzeichnis
      </button>

      {/* Company Header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-540">
        <div className="h-32 bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center" data-id="element-541">
          <span className="text-7xl drop-shadow-sm" data-id="element-542">{company.logo}</span>
        </div>
        <div className="p-6 md:p-8" data-id="element-543">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-544">
            <div data-id="element-545">
              <div className="flex items-center gap-3 mb-2" data-id="element-546">
                <h1 className="text-2xl font-bold text-slate-900" data-id="element-547">
                  {company.name}
                </h1>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600" data-id="element-548">
                  {company.industry}
                </span>
              </div>
              <p className="text-slate-600 max-w-2xl leading-relaxed" data-id="element-549">
                {company.description}
              </p>
            </div>
            <button onClick={() => onNavigate('messages')} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors shadow-sm whitespace-nowrap" data-id="element-550">
              <MessageSquareIcon size={16} data-id="element-551" />
              Kontakt aufnehmen
            </button>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 pt-6 border-t border-slate-100" data-id="element-552">
            <div className="flex items-center gap-3" data-id="element-553">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-600" data-id="element-554">
                <UsersIcon size={18} data-id="element-555" />
              </div>
              <div data-id="element-556">
                <p className="text-lg font-bold text-slate-900" data-id="element-557">
                  {company.employeeCount}
                </p>
                <p className="text-xs text-slate-500" data-id="element-558">Mitarbeiter</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-559">
              <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600" data-id="element-560">
                <Building2Icon size={18} data-id="element-561" />
              </div>
              <div data-id="element-562">
                <p className="text-lg font-bold text-slate-900" data-id="element-563">
                  {teamMembers.length}
                </p>
                <p className="text-xs text-slate-500" data-id="element-564">Im Netzwerk</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-565">
              <div className="p-2 rounded-lg bg-purple-50 text-purple-600" data-id="element-566">
                <CalendarIcon size={18} data-id="element-567" />
              </div>
              <div data-id="element-568">
                <p className="text-lg font-bold text-slate-900" data-id="element-569">
                  {new Date(company.memberSince).getFullYear()}
                </p>
                <p className="text-xs text-slate-500" data-id="element-570">Partner seit</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-571">
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600" data-id="element-572">
                <GlobeIcon size={18} data-id="element-573" />
              </div>
              <div data-id="element-574">
                <a href={`https://${company.website}`} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-id="element-575">
                  Website <ExternalLinkIcon size={12} data-id="element-576" />
                </a>
                <p className="text-xs text-slate-500" data-id="element-577">{company.website}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-578">
        <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-579">
          Leistungen & Angebote
        </h2>
        <div className="flex flex-wrap gap-2" data-id="element-580">
          {company.services.map((service, i) => <span key={i} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-sm font-medium rounded-lg border border-emerald-100" data-id="element-581">
              {service}
            </span>)}
        </div>
      </div>

      {/* Team Members */}
      <div className="space-y-4" data-id="element-582">
        <div className="flex items-center justify-between" data-id="element-583">
          <h2 className="text-lg font-bold text-slate-900" data-id="element-584">
            Team im Netzwerk ({teamMembers.length})
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-585">
          {teamMembers.map(person => <div key={person.id} onClick={() => onNavigate('person-profile', person.id)} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-all cursor-pointer group flex items-center gap-4" data-id="element-586">
              <div className="relative flex-shrink-0" data-id="element-587">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-lg font-bold text-slate-600" data-id="element-588">
                  {person.avatar}
                </div>
                {person.isOnline && <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full" data-id="element-589"></div>}
              </div>
              <div className="min-w-0" data-id="element-590">
                <h3 className="font-bold text-slate-900 group-hover:text-emerald-600 transition-colors truncate" data-id="element-591">
                  {person.firstName} {person.lastName}
                </h3>
                <p className="text-sm text-slate-500 truncate" data-id="element-592">{person.role}</p>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400" data-id="element-593">
                  <span data-id="element-594">{person.eventsAttended} Events</span>
                  <span data-id="element-595">•</span>
                  <span data-id="element-596">{person.connectionsCount} Kontakte</span>
                </div>
              </div>
            </div>)}
        </div>
        {teamMembers.length === 0 && <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center" data-id="element-597">
            <UsersIcon size={40} className="mx-auto text-slate-300 mb-3" data-id="element-598" />
            <p className="text-slate-500" data-id="element-599">
              Noch keine Teammitglieder im Netzwerk registriert.
            </p>
          </div>}
      </div>
    </div>;
}